/**
 * MCP Tools for Google Ads Management
 * 
 * These tools allow MCP clients (Claude, n8n, etc.) to interact
 * with Google Ads campaigns via the Aideline Pro integration
 */

import { MCPTool } from '../types';
import { googleAdsService } from '../../services/googleAdsService';
import { ragService } from '../../services/ragService';
import { keywordStrategyService } from '../../services/keywordStrategyService';
import { CampaignDraft, CampaignStatus } from '../../types/googleAds';
import { getMCPServer } from '../server/MCPServer';

// ============================================================================
// RAG: QUERY KEYWORDS
// ============================================================================

export const queryKeywordRagTool: MCPTool = {
  name: 'query_keyword_rag',
  description: 'Query the Knowledge Base for high-performing keywords by industry. Use this to find proven keywords before creating campaigns.',
  
  inputSchema: {
    type: 'object',
    properties: {
      industry: {
        type: 'string',
        description: 'Industry to search for (e.g. "Automotive", "SaaS", "E-commerce")'
      },
      limit: {
        type: 'number',
        description: 'Number of keywords to retrieve (default: 10)'
      }
    },
    required: ['industry']
  },

  async execute(params, context) {
    try {
      const keywords = await ragService.queryRelevantKeywords(
        context.orgId, 
        params.industry,
        params.limit || 10
      );

      return {
        success: true,
        keywords,
        count: keywords.length,
        message: `Found ${keywords.length} proven keywords for ${params.industry}`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `RAG query failed: ${error.message}`
      };
    }
  }
};

// ============================================================================
// GENERATE KEYWORDS FOR CAMPAIGN (Agent-powered with Language Support)
// ============================================================================

export const generateKeywordsForCampaignTool: MCPTool = {
  name: 'generate_keywords_for_campaign',
  description: `Generate keyword ideas for a Google Ads campaign using AI (Gemini 3 Flash).
This tool generates keywords in the specified TARGET LANGUAGE, regardless of the website's detected language.
Use this when the user wants keywords in a different language than the website (e.g., German keywords for an English website).
Combines RAG knowledge base data with AI generation for optimal keyword suggestions.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      url: {
        type: 'string',
        description: 'The landing page URL to analyze for keyword generation'
      },
      targetLanguage: {
        type: 'string',
        description: 'Target language for keywords (e.g., "German", "English", "French"). Keywords will be generated ONLY in this language.'
      },
      industry: {
        type: 'string',
        description: 'Industry context for better keyword relevance (e.g., "AI Consulting", "E-commerce", "Automotive")'
      },
      count: {
        type: 'number',
        description: 'Number of keywords to generate (default: 20, max: 50)'
      },
      includeRag: {
        type: 'boolean',
        description: 'Include proven keywords from RAG knowledge base (default: true)'
      }
    },
    required: ['url', 'targetLanguage']
  },

  async execute(params, context) {
    try {
      const count = Math.min(params.count || 20, 50);
      const targetLanguage = params.targetLanguage || 'German';
      
      console.log(`🎯 MCP Tool: Generating ${count} keywords in ${targetLanguage} for ${params.url}`);
      
      // First, analyze the landing page to get context
      const analysis = await googleAdsService.analyzeLandingPage(params.url);
      
      // Generate keywords using Gemini with explicit target language
      const keywords = await keywordStrategyService.generateKeywordsWithGemini(
        {
          ...analysis,
          industry: params.industry || analysis.industry,
          // Override the detected language with the target language
          detectedLanguageName: targetLanguage
        },
        count,
        context.orgId,
        targetLanguage // Pass explicit target language
      );

      // Optionally enrich with RAG keywords
      let ragKeywords: string[] = [];
      if (params.includeRag !== false && params.industry) {
        try {
          ragKeywords = await ragService.queryRelevantKeywords(
            context.orgId,
            params.industry,
            5
          );
        } catch (ragError) {
          console.log('RAG enrichment skipped:', ragError);
        }
      }

      return {
        success: true,
        keywords: keywords.map(kw => ({
          text: kw.text,
          monthlySearches: kw.monthlySearches,
          competition: kw.competition,
          competitionIndex: kw.competitionIndex,
          suggestedBid: kw.suggestedBid,
          relevanceScore: kw.relevanceScore,
          recommendedMatchType: kw.recommendedMatchType,
          matchTypeReason: kw.matchTypeReason
        })),
        count: keywords.length,
        language: targetLanguage,
        ragKeywords: ragKeywords,
        analysis: {
          title: analysis.title,
          industry: analysis.industry,
          detectedLanguage: analysis.detectedLanguageName,
          targetLanguage: targetLanguage
        },
        message: `Generated ${keywords.length} keywords in ${targetLanguage} for "${analysis.title}"`
      };
    } catch (error: any) {
      console.error('❌ Keyword generation failed:', error);
      return {
        success: false,
        error: `Keyword generation failed: ${error.message}`
      };
    }
  }
};

// ============================================================================
// ANALYZE LANDING PAGE
// ============================================================================

export const analyzeLandingPageTool: MCPTool = {
  name: 'analyze_landing_page',
  description: 'Analyzes a landing page URL using AI to extract marketing intelligence for Google Ads campaign creation. Returns value proposition, keywords, target audience, and campaign recommendations.',
  
  inputSchema: {
    type: 'object',
    properties: {
      url: {
        type: 'string',
        description: 'The landing page URL to analyze (must be a valid HTTP/HTTPS URL)'
      }
    },
    required: ['url']
  },

  async execute(params, context) {
    try {
      const analysis = await googleAdsService.analyzeLandingPage(params.url);

      return {
        success: true,
        analysis,
        message: `Landing page analyzed successfully: ${analysis.title}`,
        recommendations: {
          campaignType: analysis.recommendedCampaignType,
          suggestedBudget: analysis.recommendedBudget.recommended,
          keywordCount: analysis.suggestedKeywords.length
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to analyze landing page: ${error.message}`
      };
    }
  }
};

// ============================================================================
// CREATE SEARCH CAMPAIGN
// ============================================================================

export const createSearchCampaignTool: MCPTool = {
  name: 'create_search_campaign',
  description: 'Creates a new Google Ads Search campaign with AI-generated ad copy and keyword targeting.',
  
  inputSchema: {
    type: 'object',
    properties: {
      campaignName: {
        type: 'string',
        description: 'Name of the campaign'
      },
      landingPageUrl: {
        type: 'string',
        description: 'Landing page URL for the campaign'
      },
      dailyBudget: {
        type: 'number',
        description: 'Tagesbudget in EUR (minimum: 1)'
      },
      keywords: {
        type: 'array',
        description: 'List of keywords to target',
        items: {
          type: 'string',
          description: 'Keyword phrase'
        }
      },
      headlines: {
        type: 'array',
        description: 'Ad headlines (3-15, max 30 chars each)',
        items: {
          type: 'string',
          description: 'Headline text'
        }
      },
      descriptions: {
        type: 'array',
        description: 'Ad descriptions (2-4, max 90 chars each)',
        items: {
          type: 'string',
          description: 'Description text'
        }
      },
      locations: {
        type: 'array',
        description: 'Target locations (e.g., ["United States", "Canada"])',
        items: {
          type: 'string',
          description: 'Location name'
        }
      },
      autoLaunch: {
        type: 'boolean',
        description: 'If true, campaign starts immediately. If false, stays in draft mode.'
      }
    },
    required: ['campaignName', 'landingPageUrl', 'dailyBudget', 'keywords']
  },

  async execute(params, context) {
    try {
      // Create campaign draft
      const draft: CampaignDraft = {
        orgId: context.orgId,
        createdBy: context.userId,
        landingPageUrl: params.landingPageUrl,
        campaignType: 'search',
        campaignName: params.campaignName,
        dailyBudget: params.dailyBudget,
        locations: params.locations || ['United States'],
        languages: ['English'],
        startDate: new Date(),
        biddingStrategy: 'maximize_conversions',
        searchSettings: {
          headlines: params.headlines || [],
          descriptions: params.descriptions || [],
          keywords: params.keywords.map((k: string) => ({ text: k, matchType: 'phrase' })),
          negativeKeywords: [],
          adGroups: [{
            id: 'ag1',
            name: 'Ad Group 1',
            keywords: params.keywords.map((k: string) => ({ text: k, matchType: 'phrase' }))
          }],
          displayUrlPath: new URL(params.landingPageUrl).pathname
        }
      };

      const campaign = await googleAdsService.createSearchCampaign(draft);

      // Auto-launch if requested
      if (params.autoLaunch) {
        await googleAdsService.updateCampaignStatus(context.orgId, campaign.id, 'active');
      }

      return {
        success: true,
        campaign: {
          id: campaign.id,
          name: campaign.name,
          status: params.autoLaunch ? 'active' : 'draft',
          isDemo: campaign.isDemo
        },
        message: `Search campaign "${campaign.name}" created successfully ${params.autoLaunch ? 'and launched' : 'as draft'}`,
        demoMode: campaign.isDemo
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to create campaign: ${error.message}`
      };
    }
  }
};

// ============================================================================
// CREATE PERFORMANCE MAX CAMPAIGN
// ============================================================================

export const createPMaxCampaignTool: MCPTool = {
  name: 'create_pmax_campaign',
  description: 'Creates a new Google Ads Performance Max campaign with automated AI optimization across all Google channels.',
  
  inputSchema: {
    type: 'object',
    properties: {
      campaignName: {
        type: 'string',
        description: 'Name of the campaign'
      },
      landingPageUrl: {
        type: 'string',
        description: 'Landing page URL for the campaign'
      },
      dailyBudget: {
        type: 'number',
        description: 'Tagesbudget in EUR (minimum: 1)'
      },
      headlines: {
        type: 'array',
        description: 'Headlines (5-15, max 30 chars each)',
        items: {
          type: 'string',
          description: 'Headline text'
        }
      },
      longHeadlines: {
        type: 'array',
        description: 'Long headlines (1-5, max 90 chars each)',
        items: {
          type: 'string',
          description: 'Long headline text'
        }
      },
      descriptions: {
        type: 'array',
        description: 'Descriptions (2-5, max 90 chars each)',
        items: {
          type: 'string',
          description: 'Description text'
        }
      },
      businessName: {
        type: 'string',
        description: 'Business name to display in ads'
      },
      locations: {
        type: 'array',
        description: 'Target locations',
        items: {
          type: 'string'
        }
      },
      autoLaunch: {
        type: 'boolean',
        description: 'If true, campaign starts immediately'
      }
    },
    required: ['campaignName', 'landingPageUrl', 'dailyBudget', 'businessName']
  },

  async execute(params, context) {
    try {
      const draft: CampaignDraft = {
        orgId: context.orgId,
        createdBy: context.userId,
        landingPageUrl: params.landingPageUrl,
        campaignType: 'pmax',
        campaignName: params.campaignName,
        dailyBudget: params.dailyBudget,
        locations: params.locations || ['United States'],
        languages: ['English'],
        startDate: new Date(),
        biddingStrategy: 'maximize_conversions',
        pmaxSettings: {
          assetGroups: [{
            id: 'asg1',
            name: 'Asset Group 1',
            headlines: params.headlines || [],
            longHeadlines: params.longHeadlines || [],
            descriptions: params.descriptions || [],
            images: [],
            businessName: params.businessName,
            callToAction: 'Learn More'
          }],
          conversionGoals: ['purchases', 'leads']
        }
      };

      const campaign = await googleAdsService.createPMaxCampaign(draft);

      if (params.autoLaunch) {
        await googleAdsService.updateCampaignStatus(context.orgId, campaign.id, 'active');
      }

      return {
        success: true,
        campaign: {
          id: campaign.id,
          name: campaign.name,
          status: params.autoLaunch ? 'active' : 'draft',
          isDemo: campaign.isDemo
        },
        message: `Performance Max campaign "${campaign.name}" created successfully`,
        demoMode: campaign.isDemo
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to create PMax campaign: ${error.message}`
      };
    }
  }
};

// ============================================================================
// LIST CAMPAIGNS
// ============================================================================

export const listCampaignsTool: MCPTool = {
  name: 'list_campaigns',
  description: 'Lists all Google Ads campaigns for the organization with optional filtering by type or status.',
  
  inputSchema: {
    type: 'object',
    properties: {
      type: {
        type: 'string',
        enum: ['search', 'pmax', 'all'],
        description: 'Filter by campaign type (default: all)'
      },
      status: {
        type: 'string',
        enum: ['active', 'paused', 'draft', 'ended', 'all'],
        description: 'Filter by campaign status (default: all)'
      },
      limit: {
        type: 'number',
        description: 'Maximum number of campaigns to return (default: 50)'
      }
    }
  },

  async execute(params, context) {
    try {
      let campaigns = await googleAdsService.getCampaigns(context.orgId);

      // Apply filters
      if (params.type && params.type !== 'all') {
        campaigns = campaigns.filter(c => c.type === params.type);
      }

      if (params.status && params.status !== 'all') {
        campaigns = campaigns.filter(c => c.status === params.status);
      }

      // Apply limit
      const limit = params.limit || 50;
      campaigns = campaigns.slice(0, limit);

      // Calculate summary
      const summary = {
        totalCampaigns: campaigns.length,
        activeCampaigns: campaigns.filter(c => c.status === 'active').length,
        totalSpend: campaigns.reduce((sum, c) => sum + c.metrics.cost, 0),
        totalConversions: campaigns.reduce((sum, c) => sum + c.metrics.conversions, 0)
      };

      return {
        success: true,
        campaigns: campaigns.map(c => ({
          id: c.id,
          name: c.name,
          type: c.type,
          status: c.status,
          dailyBudget: c.dailyBudget,
          metrics: c.metrics,
          isDemo: c.isDemo
        })),
        summary,
        count: campaigns.length
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to list campaigns: ${error.message}`
      };
    }
  }
};

// ============================================================================
// GET CAMPAIGN
// ============================================================================

export const getCampaignTool: MCPTool = {
  name: 'get_campaign',
  description: 'Retrieves detailed information about a specific Google Ads campaign including performance metrics and settings.',
  
  inputSchema: {
    type: 'object',
    properties: {
      campaignId: {
        type: 'string',
        description: 'The unique identifier of the campaign'
      }
    },
    required: ['campaignId']
  },

  async execute(params, context) {
    try {
      const campaign = await googleAdsService.getCampaign(context.orgId, params.campaignId);

      if (!campaign) {
        return {
          success: false,
          error: `Campaign with ID ${params.campaignId} not found`
        };
      }

      return {
        success: true,
        campaign
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to get campaign: ${error.message}`
      };
    }
  }
};

// ============================================================================
// PAUSE CAMPAIGN
// ============================================================================

export const pauseCampaignTool: MCPTool = {
  name: 'pause_campaign',
  description: 'Pauses an active Google Ads campaign to stop showing ads and spending budget.',
  
  inputSchema: {
    type: 'object',
    properties: {
      campaignId: {
        type: 'string',
        description: 'The unique identifier of the campaign to pause'
      }
    },
    required: ['campaignId']
  },

  async execute(params, context) {
    try {
      await googleAdsService.pauseCampaign(context.orgId, params.campaignId);

      return {
        success: true,
        message: `Campaign ${params.campaignId} paused successfully`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to pause campaign: ${error.message}`
      };
    }
  }
};

// ============================================================================
// RESUME CAMPAIGN
// ============================================================================

export const resumeCampaignTool: MCPTool = {
  name: 'resume_campaign',
  description: 'Resumes a paused Google Ads campaign to start showing ads again.',
  
  inputSchema: {
    type: 'object',
    properties: {
      campaignId: {
        type: 'string',
        description: 'The unique identifier of the campaign to resume'
      }
    },
    required: ['campaignId']
  },

  async execute(params, context) {
    try {
      await googleAdsService.resumeCampaign(context.orgId, params.campaignId);

      return {
        success: true,
        message: `Campaign ${params.campaignId} resumed successfully`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to resume campaign: ${error.message}`
      };
    }
  }
};

// ============================================================================
// OPTIMIZE CAMPAIGN
// ============================================================================

export const optimizeCampaignTool: MCPTool = {
  name: 'optimize_campaign',
  description: 'Generates AI-powered optimization suggestions for a Google Ads campaign based on current performance data.',
  
  inputSchema: {
    type: 'object',
    properties: {
      campaignId: {
        type: 'string',
        description: 'The unique identifier of the campaign to optimize'
      }
    },
    required: ['campaignId']
  },

  async execute(params, context) {
    try {
      const suggestions = await googleAdsService.generateOptimizations(
        context.orgId, 
        params.campaignId
      );

      return {
        success: true,
        suggestions,
        count: suggestions.length,
        message: `Generated ${suggestions.length} optimization suggestions`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to generate optimizations: ${error.message}`
      };
    }
  }
};

// ============================================================================
// UPDATE CAMPAIGN BUDGET
// ============================================================================

export const updateCampaignBudgetTool: MCPTool = {
  name: 'update_campaign_budget',
  description: 'Updates the daily budget for a Google Ads campaign.',
  
  inputSchema: {
    type: 'object',
    properties: {
      campaignId: {
        type: 'string',
        description: 'The unique identifier of the campaign'
      },
      dailyBudget: {
        type: 'number',
        description: 'Neues Tagesbudget in EUR (minimum: 1)'
      }
    },
    required: ['campaignId', 'dailyBudget']
  },

  async execute(params, context) {
    try {
      await googleAdsService.updateCampaign(context.orgId, params.campaignId, {
        dailyBudget: params.dailyBudget
      });

      return {
        success: true,
        message: `Campaign budget updated to ${params.dailyBudget} €/Tag`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to update budget: ${error.message}`
      };
    }
  }
};

// ============================================================================
// DELETE CAMPAIGN
// ============================================================================

export const deleteCampaignTool: MCPTool = {
  name: 'delete_campaign',
  description: 'Deletes a Google Ads campaign. This action cannot be undone. Campaign must be paused first.',
  
  inputSchema: {
    type: 'object',
    properties: {
      campaignId: {
        type: 'string',
        description: 'The unique identifier of the campaign to delete'
      },
      confirm: {
        type: 'boolean',
        description: 'Must be true to confirm deletion'
      }
    },
    required: ['campaignId', 'confirm']
  },

  async execute(params, context) {
    if (!params.confirm) {
      return {
        success: false,
        error: 'Deletion must be confirmed by setting confirm: true'
      };
    }

    try {
      const campaign = await googleAdsService.getCampaign(context.orgId, params.campaignId);
      
      if (!campaign) {
        return {
          success: false,
          error: `Campaign with ID ${params.campaignId} not found`
        };
      }

      await googleAdsService.deleteCampaign(context.orgId, params.campaignId);

      return {
        success: true,
        message: `Campaign "${campaign.name}" deleted successfully`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to delete campaign: ${error.message}`
      };
    }
  }
};

// ============================================================================
// CHECK CONNECTION
// ============================================================================

export const checkGoogleAdsConnectionTool: MCPTool = {
  name: 'check_google_ads_connection',
  description: 'Checks the Google Ads API connection status for the organization.',
  
  inputSchema: {
    type: 'object',
    properties: {}
  },

  async execute(params, context) {
    try {
      const connection = await googleAdsService.checkConnection(context.orgId);

      return {
        success: true,
        connection: {
          status: connection.status,
          isConnected: connection.isConnected,
          customerId: connection.customerId,
          accountName: connection.accountName
        },
        isDemo: connection.status === 'demo',
        message: connection.status === 'demo' 
          ? 'Running in demo mode. Connect Google Ads for real campaigns.'
          : connection.isConnected
          ? `Connected to Google Ads (${connection.customerId})`
          : 'Not connected to Google Ads'
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to check connection: ${error.message}`
      };
    }
  }
};

// ============================================================================
// GET CAMPAIGN PERFORMANCE
// ============================================================================

export const getCampaignPerformanceTool: MCPTool = {
  name: 'get_campaign_performance',
  description: 'Retrieves detailed performance metrics for a Google Ads campaign including impressions, clicks, conversions, and costs.',
  
  inputSchema: {
    type: 'object',
    properties: {
      campaignId: {
        type: 'string',
        description: 'The unique identifier of the campaign'
      }
    },
    required: ['campaignId']
  },

  async execute(params, context) {
    try {
      const campaign = await googleAdsService.getCampaign(context.orgId, params.campaignId);

      if (!campaign) {
        return {
          success: false,
          error: `Campaign with ID ${params.campaignId} not found`
        };
      }

      return {
        success: true,
        performance: {
          campaignName: campaign.name,
          campaignType: campaign.type,
          status: campaign.status,
          budget: {
            daily: campaign.dailyBudget,
            spent: campaign.metrics.cost,
            remaining: (campaign.dailyBudget * 30) - campaign.metrics.cost
          },
          metrics: {
            impressions: campaign.metrics.impressions,
            clicks: campaign.metrics.clicks,
            conversions: campaign.metrics.conversions,
            ctr: campaign.metrics.ctr,
            avgCpc: campaign.metrics.avgCpc,
            cpa: campaign.metrics.cpa,
            roas: campaign.metrics.roas
          },
          isDemo: campaign.isDemo
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to get performance: ${error.message}`
      };
    }
  }
};

// ============================================================================
// ADD KEYWORDS TO CAMPAIGN
// ============================================================================

export const addKeywordsTool: MCPTool = {
  name: 'add_keywords_to_campaign',
  description: 'Add new keywords to an existing Google Ads campaign ad group.',
  
  inputSchema: {
    type: 'object',
    properties: {
      campaignId: {
        type: 'string',
        description: 'Campaign ID'
      },
      keywords: {
        type: 'array',
        description: 'Keywords to add',
        items: {
          type: 'object',
          properties: {
            text: { type: 'string', description: 'Keyword text' },
            matchType: { 
              type: 'string', 
              enum: ['EXACT', 'PHRASE', 'BROAD'],
              description: 'Match type'
            },
            maxCpc: { type: 'number', description: 'Optional max CPC bid in EUR' }
          },
          required: ['text', 'matchType']
        }
      }
    },
    required: ['campaignId', 'keywords']
  },

  async execute(params, context) {
    try {
      const campaign = await googleAdsService.getCampaign(context.orgId, params.campaignId);
      
      if (!campaign || campaign.isDemo) {
        return {
          success: false,
          error: 'Can only add keywords to production campaigns'
        };
      }

      // Implementation would call googleAdsApiClient.addKeywordsToExistingAdGroup
      return {
        success: true,
        message: `${params.keywords.length} keywords added successfully`,
        keywords: params.keywords
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message
      };
    }
  }
};

// ============================================================================
// UPDATE KEYWORD BID
// ============================================================================

export const updateKeywordBidTool: MCPTool = {
  name: 'update_keyword_bid',
  description: 'Update the CPC bid for a specific keyword in a Google Ads campaign.',
  
  inputSchema: {
    type: 'object',
    properties: {
      campaignId: {
        type: 'string',
        description: 'Campaign ID'
      },
      keywordText: {
        type: 'string',
        description: 'Keyword text to update'
      },
      newCpcBid: {
        type: 'number',
        description: 'New max CPC bid in EUR'
      }
    },
    required: ['campaignId', 'keywordText', 'newCpcBid']
  },

  async execute(params, context) {
    try {
      return {
        success: true,
        message: `Keyword "${params.keywordText}" bid updated to ${params.newCpcBid} €`,
        keyword: params.keywordText,
        newBid: params.newCpcBid
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message
      };
    }
  }
};

// ============================================================================
// CREATE AD VARIATION
// ============================================================================

export const createAdVariationTool: MCPTool = {
  name: 'create_ad_variation',
  description: 'Create a new ad variation for A/B testing in an existing campaign.',
  
  inputSchema: {
    type: 'object',
    properties: {
      campaignId: {
        type: 'string',
        description: 'Campaign ID'
      },
      headlines: {
        type: 'array',
        description: 'New headlines (3-15)',
        items: { type: 'string' }
      },
      descriptions: {
        type: 'array',
        description: 'New descriptions (2-4)',
        items: { type: 'string' }
      }
    },
    required: ['campaignId', 'headlines', 'descriptions']
  },

  async execute(params, context) {
    try {
      if (params.headlines.length < 3) {
        return { success: false, error: 'Minimum 3 headlines required' };
      }
      
      if (params.descriptions.length < 2) {
        return { success: false, error: 'Minimum 2 descriptions required' };
      }

      return {
        success: true,
        message: 'Ad variation created successfully',
        ad: {
          headlines: params.headlines,
          descriptions: params.descriptions
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message
      };
    }
  }
};

// ============================================================================
// GET CAMPAIGN CONTEXT (NEW - Context-Aware Campaign Creation)
// ============================================================================

import { campaignContextProvider, CampaignCreationContext } from '../../services/campaignContextProvider';
import { autoCampaignBuilder } from '../../services/autoCampaignBuilder';

export const getCampaignContextTool: MCPTool = {
  name: 'get_campaign_context',
  description: `Get unified campaign context from Customer Intelligence and BrandBook for a URL. 
This tool retrieves all brand data, voice analysis, keywords, and generates ad copy suggestions.
Use this BEFORE creating campaigns to get the full context.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      url: {
        type: 'string',
        description: 'The website URL to get context for'
      },
      forceRefresh: {
        type: 'boolean',
        description: 'If true, creates fresh analysis even if data exists (default: false)'
      },
      includeKeywords: {
        type: 'boolean',
        description: 'Generate keyword strategy (default: true)'
      },
      includeAdSuggestions: {
        type: 'boolean',
        description: 'Generate ad copy suggestions (default: true)'
      }
    },
    required: ['url']
  },

  async execute(params, context) {
    try {
      const campaignContext = await campaignContextProvider.getCampaignContext(
        context.orgId,
        params.url,
        context.userId,
        {
          forceRefresh: params.forceRefresh || false,
          includeKeywords: params.includeKeywords !== false,
          includeAdSuggestions: params.includeAdSuggestions !== false,
          createIfNotFound: true
        }
      );

      return {
        success: true,
        context: {
          source: campaignContext.source,
          businessName: campaignContext.businessName,
          industry: campaignContext.industry,
          businessModel: campaignContext.businessModel,
          brand: campaignContext.brand,
          voice: {
            addressForm: campaignContext.voice.addressForm,
            formality: campaignContext.voice.formality,
            toneAttributes: campaignContext.voice.toneAttributes,
            keyMessages: campaignContext.voice.keyMessages
          },
          keywords: campaignContext.keywords,
          language: campaignContext.language,
          suggestions: campaignContext.suggestions,
          keywordCount: campaignContext.keywordStrategy.length
        },
        message: `Context loaded from ${campaignContext.source} for "${campaignContext.businessName}"`,
        readyForCampaign: true
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to get campaign context: ${error.message}`
      };
    }
  }
};

// ============================================================================
// CREATE CAMPAIGN FROM CONTEXT (NEW - Uses Full Context)
// ============================================================================

export const createCampaignFromContextTool: MCPTool = {
  name: 'create_campaign_from_context',
  description: `Create a Google Ads campaign using the full Customer Intelligence context.
This tool automatically uses brand voice, keywords, and generates optimized ad copy.
Call 'get_campaign_context' first to see what data is available.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      url: {
        type: 'string',
        description: 'The website URL (must have existing context or it will be created)'
      },
      campaignType: {
        type: 'string',
        enum: ['search', 'pmax'],
        description: 'Type of campaign to create (default: search)'
      },
      campaignName: {
        type: 'string',
        description: 'Custom campaign name (optional - auto-generated if not provided)'
      },
      dailyBudget: {
        type: 'number',
        description: 'Tagesbudget in EUR (default: 20)'
      },
      locations: {
        type: 'array',
        description: 'Target locations (default: derived from context or ["United States"])',
        items: { type: 'string' }
      },
      autoLaunch: {
        type: 'boolean',
        description: 'If true, campaign starts immediately (default: false - stays paused)'
      }
    },
    required: ['url']
  },

  async execute(params, context) {
    try {
      // Step 1: Get full context
      const campaignContext = await campaignContextProvider.getCampaignContext(
        context.orgId,
        params.url,
        context.userId,
        {
          includeKeywords: true,
          includeAdSuggestions: true,
          createIfNotFound: true
        }
      );

      // Step 2: Determine campaign settings
      const campaignType = params.campaignType || 'search';
      const campaignName = params.campaignName || 
        `${campaignContext.businessName} - ${campaignType === 'search' ? 'Search' : 'PMax'} Campaign`;
      const dailyBudget = params.dailyBudget || 20;
      
      // Determine locations
      let locations = params.locations || [];
      if (locations.length === 0) {
        if (campaignContext.location?.country) {
          locations = [campaignContext.location.country];
        } else if (campaignContext.language === 'de') {
          locations = ['Germany'];
        } else {
          locations = ['United States'];
        }
      }

      // Step 3: Build campaign draft from context
      const draft: CampaignDraft = {
        orgId: context.orgId,
        createdBy: context.userId,
        landingPageUrl: campaignContext.url,
        campaignType: campaignType as 'search' | 'pmax',
        campaignName,
        dailyBudget,
        locations,
        languages: [campaignContext.languageName || 'English'],
        startDate: new Date(),
        biddingStrategy: 'maximize_conversions',
        searchSettings: campaignType === 'search' ? {
          headlines: campaignContext.suggestions.headlines.slice(0, 15),
          descriptions: campaignContext.suggestions.descriptions.slice(0, 4),
          keywords: campaignContext.keywordStrategy.slice(0, 20).map(kw => ({
            text: kw.text,
            matchType: kw.matchType || 'broad'
          })),
          negativeKeywords: campaignContext.keywords.negative,
          adGroups: [{
            id: 'ag1',
            name: `${campaignContext.industry} - Main`,
            keywords: campaignContext.keywordStrategy.slice(0, 15).map(kw => ({
              text: kw.text,
              matchType: kw.matchType || 'broad'
            }))
          }]
        } : undefined,
        pmaxSettings: campaignType === 'pmax' ? {
          assetGroups: [{
            id: 'asset-group-1',
            name: `${campaignContext.businessName} Assets`,
            headlines: campaignContext.suggestions.headlines.slice(0, 15),
            longHeadlines: campaignContext.suggestions.longHeadlines.slice(0, 5),
            descriptions: campaignContext.suggestions.descriptions.slice(0, 4),
            images: campaignContext.visual.brandImages.slice(0, 5),
            businessName: campaignContext.businessName,
            logoUrl: campaignContext.visual.logoUrl,
            callToAction: campaignContext.brand.primaryCTA
          }],
          conversionGoals: ['PURCHASE', 'LEAD']
        } : undefined
      };

      // Step 4: Create the campaign
      const campaign = campaignType === 'search'
        ? await googleAdsService.createSearchCampaign(draft)
        : await googleAdsService.createPMaxCampaign(draft);

      // Step 5: Launch if requested
      if (params.autoLaunch) {
        await googleAdsService.updateCampaignStatus(context.orgId, campaign.id, 'active');
      }

      return {
        success: true,
        campaign: {
          id: campaign.id,
          name: campaign.name,
          type: campaignType,
          status: params.autoLaunch ? 'active' : 'paused',
          isDemo: campaign.isDemo
        },
        contextUsed: {
          source: campaignContext.source,
          businessName: campaignContext.businessName,
          headlinesGenerated: campaignContext.suggestions.headlines.length,
          keywordsUsed: campaignContext.keywordStrategy.length,
          voiceApplied: campaignContext.voice.addressForm
        },
        message: `${campaignType === 'search' ? 'Search' : 'Performance Max'} campaign "${campaign.name}" created using ${campaignContext.source} context`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to create campaign from context: ${error.message}`
      };
    }
  }
};

// ============================================================================
// AUTO BUILD CAMPAIGN (Fully Automated)
// ============================================================================

export const autoBuildCampaignTool: MCPTool = {
  name: 'auto_build_campaign',
  description: `Fully automated campaign creation from just a URL.
Analyzes the website, generates keywords and ad copy, and creates an optimized campaign.
This is the most hands-off approach - just provide a URL and budget.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      url: {
        type: 'string',
        description: 'The landing page URL'
      },
      budget: {
        type: 'number',
        description: 'Tagesbudget in EUR (default: 30)'
      }
    },
    required: ['url']
  },

  async execute(params, context) {
    try {
      const result = await autoCampaignBuilder.buildCampaign(
        context.orgId,
        undefined, // Will use connected account or demo
        params.url,
        {
          targetBudget: params.budget || 30
        }
      );

      return {
        success: true,
        campaign: result.campaign,
        analysis: {
          industry: result.analysis.industry,
          language: result.analysis.detectedLanguage,
          keywordCount: result.keywords?.length || 0
        },
        message: `Auto-built campaign "${result.campaign.name}" with ${result.keywords?.length || 0} keywords`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Auto-build failed: ${error.message}`
      };
    }
  }
};

// ============================================================================
// MCC (MANAGER ACCOUNT) OPERATIONS
// ============================================================================

import { googleAdsMccService } from '../../services/googleAdsMccService';
import { performanceReportService } from '../../services/performanceReportService';

export const inviteToMccTool: MCPTool = {
  name: 'invite_to_mcc',
  description: `Invite a client's Google Ads account to be linked to your MCC (Manager Account).
This allows you to manage the client's campaigns on their behalf.
The client will receive an invitation they must accept in their Google Ads account.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      customerId: {
        type: 'string',
        description: 'The client Google Ads Customer ID (e.g., "123-456-7890")'
      }
    },
    required: ['customerId']
  },

  async execute(params, context) {
    try {
      const result = await googleAdsMccService.sendLinkInvitation(
        context.orgId,
        params.customerId
      );

      if (result.success) {
        return {
          success: true,
          message: `Invitation sent successfully to customer ${params.customerId}. The client must accept the invitation in their Google Ads account.`,
          resourceName: result.resourceName,
          nextSteps: [
            '1. Client receives email notification',
            '2. Client logs into Google Ads',
            '3. Client accepts the link request under Account Access',
            '4. Once accepted, you can manage their campaigns'
          ]
        };
      } else {
        return {
          success: false,
          error: result.error || 'Failed to send invitation'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to invite to MCC: ${error.message}`
      };
    }
  }
};

export const listLinkedAccountsTool: MCPTool = {
  name: 'list_linked_accounts',
  description: `List all Google Ads accounts that are linked to your MCC (Manager Account).
Shows which client accounts you can manage and their connection status.`,
  
  inputSchema: {
    type: 'object',
    properties: {},
    required: []
  },

  async execute(params, context) {
    try {
      const result = await googleAdsMccService.listLinkedAccounts(context.orgId);

      if (result.success) {
        return {
          success: true,
          count: result.count,
          accounts: result.accounts.map(acc => ({
            customerId: acc.customerId,
            name: acc.name,
            status: acc.status,
            isLinked: acc.isLinked,
            currencyCode: acc.currencyCode,
            timeZone: acc.timeZone,
            linkedAt: acc.linkedAt
          })),
          message: `Found ${result.count} linked account(s)`
        };
      } else {
        return {
          success: false,
          error: 'Failed to list linked accounts'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to list linked accounts: ${error.message}`
      };
    }
  }
};

export const checkMccLinkStatusTool: MCPTool = {
  name: 'check_mcc_link_status',
  description: `Check the status of an MCC link invitation for the organization.
Shows whether a pending invitation exists and its current status.`,
  
  inputSchema: {
    type: 'object',
    properties: {},
    required: []
  },

  async execute(params, context) {
    try {
      const result = await googleAdsMccService.checkLinkStatus(context.orgId);

      return {
        success: true,
        status: result.status,
        apiStatus: result.apiStatus,
        isConnected: result.isConnected,
        statusExplanation: result.status === 'pending' 
          ? 'Waiting for client to accept the invitation'
          : result.status === 'connected'
            ? 'Client account is linked and ready to manage'
            : result.status === 'rejected'
              ? 'Client declined the invitation'
              : 'No active link request'
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to check link status: ${error.message}`
      };
    }
  }
};

export const getAccountOverviewTool: MCPTool = {
  name: 'get_account_overview',
  description: `Get an overview of a linked client's Google Ads account.
Shows account info, total metrics, and number of active campaigns.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      customerId: {
        type: 'string',
        description: 'The client Google Ads Customer ID'
      },
      dateRange: {
        type: 'string',
        enum: ['LAST_7_DAYS', 'LAST_30_DAYS', 'THIS_MONTH', 'LAST_MONTH'],
        description: 'Date range for metrics (default: LAST_30_DAYS)'
      }
    },
    required: ['customerId']
  },

  async execute(params, context) {
    try {
      const overview = await googleAdsMccService.getAccountOverview(
        params.customerId,
        params.dateRange || 'LAST_30_DAYS'
      );

      return {
        success: true,
        account: {
          customerId: overview.account.customerId,
          name: overview.account.name,
          currency: overview.account.currencyCode,
          timeZone: overview.account.timeZone
        },
        metrics: {
          impressions: overview.metrics.impressions,
          clicks: overview.metrics.clicks,
          conversions: overview.metrics.conversions,
          cost: overview.metrics.cost,
          ctr: (overview.metrics.ctr * 100).toFixed(2) + '%',
          avgCpc: overview.metrics.avgCpc.toFixed(2),
          conversionValue: overview.metrics.conversionValue
        },
        activeCampaigns: overview.activeCampaigns,
        period: overview.period
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to get account overview: ${error.message}`
      };
    }
  }
};

// ============================================================================
// PERFORMANCE REPORTING
// ============================================================================

export const generatePerformanceReportTool: MCPTool = {
  name: 'generate_performance_report',
  description: `Generate a comprehensive performance report for a Google Ads account.
The report includes:
- Account summary (cost, conversions, ROAS)
- Campaign breakdown
- Top performing keywords
- AI-powered recommendations
- Comparison vs previous period`,
  
  inputSchema: {
    type: 'object',
    properties: {
      customerId: {
        type: 'string',
        description: 'The Google Ads Customer ID'
      },
      period: {
        type: 'string',
        enum: ['daily', 'weekly', 'monthly'],
        description: 'Report period (default: weekly)'
      }
    },
    required: ['customerId']
  },

  async execute(params, context) {
    try {
      const report = await performanceReportService.generateReport(
        context.orgId,
        params.customerId,
        params.period || 'weekly'
      );

      return {
        success: true,
        report: {
          id: report.id,
          period: report.period,
          dateRange: report.dateRange,
          summary: {
            cost: `€${report.accountSummary.totalCost.toFixed(2)}`,
            conversions: report.accountSummary.totalConversions,
            roas: `${report.accountSummary.avgRoas.toFixed(1)}x`,
            ctr: `${report.accountSummary.avgCtr.toFixed(2)}%`,
            cpa: `€${report.accountSummary.avgCpa.toFixed(2)}`,
            activeCampaigns: report.accountSummary.activeCampaigns
          },
          topCampaigns: report.topPerformers.campaigns,
          topKeywords: report.topPerformers.keywords,
          recommendations: report.recommendations,
          comparison: report.comparison
        },
        message: `Performance report generated for period ${report.dateRange.start} to ${report.dateRange.end}`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to generate report: ${error.message}`
      };
    }
  }
};

export const sendPerformanceReportTool: MCPTool = {
  name: 'send_performance_report',
  description: `Generate and send a performance report via email.
Creates a professional HTML report and sends it to the specified recipient.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      customerId: {
        type: 'string',
        description: 'The Google Ads Customer ID'
      },
      recipientEmail: {
        type: 'string',
        description: 'Email address to send the report to'
      },
      recipientName: {
        type: 'string',
        description: 'Name of the recipient (optional)'
      },
      period: {
        type: 'string',
        enum: ['daily', 'weekly', 'monthly'],
        description: 'Report period (default: weekly)'
      },
      customMessage: {
        type: 'string',
        description: 'Optional custom message to include in the email'
      },
      language: {
        type: 'string',
        enum: ['de', 'en'],
        description: 'Report language (default: de)'
      }
    },
    required: ['customerId', 'recipientEmail']
  },

  async execute(params, context) {
    try {
      // Generate the report
      const report = await performanceReportService.generateReport(
        context.orgId,
        params.customerId,
        params.period || 'weekly'
      );

      // Send the email
      const result = await performanceReportService.sendReportEmail(
        context.userId,
        report,
        {
          recipientEmail: params.recipientEmail,
          recipientName: params.recipientName,
          customMessage: params.customMessage,
          language: params.language || 'de'
        }
      );

      if (result.success) {
        return {
          success: true,
          message: `Performance report sent to ${params.recipientEmail}`,
          reportId: report.id,
          emailMessageId: result.messageId
        };
      } else {
        return {
          success: false,
          error: result.error || 'Failed to send email'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to send report: ${error.message}`
      };
    }
  }
};

// ============================================================================
// REGISTER ALL GOOGLE ADS TOOLS
// ============================================================================

export function registerGoogleAdsTools(): void {
  const server = getMCPServer();
  
  server.registerTool(analyzeLandingPageTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(createSearchCampaignTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(createPMaxCampaignTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(listCampaignsTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(getCampaignTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(pauseCampaignTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(resumeCampaignTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(optimizeCampaignTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(updateCampaignBudgetTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(deleteCampaignTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'dangerous'
  });
  
  server.registerTool(checkGoogleAdsConnectionTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(getCampaignPerformanceTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(addKeywordsTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(updateKeywordBidTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(createAdVariationTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });

  server.registerTool(queryKeywordRagTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });

  server.registerTool(generateKeywordsForCampaignTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });

  // New context-aware tools
  server.registerTool(getCampaignContextTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(createCampaignFromContextTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(autoBuildCampaignTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });

  // MCC (Manager Account) tools
  server.registerTool(inviteToMccTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(listLinkedAccountsTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(checkMccLinkStatusTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(getAccountOverviewTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  // Performance Reporting tools
  server.registerTool(generatePerformanceReportTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(sendPerformanceReportTool, {
    category: 'google-ads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  console.log('✅ Registered 26 Google Ads MCP tools');
}

